import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { filter, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CustomHeader } from '../models/custom-header.enum';

/**
 * BaseApiService provides a centralized way to interact with backend APIs.
 * It includes methods for common HTTP operations such as GET, POST, PUT, PATCH, and DELETE.
 * This service automatically appends the organization UUID to all requests.
 */
@Injectable({ providedIn: 'root' })
export class BaseApiService {
    // Injects the Angular HttpClient for making HTTP requests.
    protected http = inject(HttpClient);

    /**
     * Performs a GET request to the specified endpoint.
     * @param endpoint The API endpoint to call.
     * @param params Optional query parameters to include in the request.
     * @param headers Optional extra headers, merged with the default ones.
     * @returns An Observable of the response body.
     */
    get<T>(endpoint: string, params: Record<string, string | number | boolean> = {}, headers: Record<string, string> = {}): Observable<T> {
        return this.http.get<T>(endpoint, {
            headers: { 'Content-Type': 'application/json', ...headers },
            params: { ...params, organizationUUID: environment.organizationUUID },
        });
    }

    /**
     * Performs a GET request to retrieve paginated data.
     * @param endpoint The API endpoint to call.
     * @param params Optional query parameters to include in the request.
     * @returns An Observable containing the result array and pagination information.
     */
    getPaginated<T>(
        endpoint: string,
        params: Record<string, string | number | boolean> = {}
    ): Observable<{
        result: T[];
        paginationInfo: { pageNumber: number; pageSize: number; totalElements: number };
    }> {
        const headers = {
            'Content-Type': 'application/json',
        };
        return this.http
            .get<T[]>(endpoint, { headers, params: { ...params, organizationUUID: environment.organizationUUID }, observe: 'response' })
            .pipe(
                filter((resp) => resp instanceof HttpResponse),
                map((data) => {
                    const response = data;

                    const rows: T[] = response.body ?? [];
                    let paginationInfoResult = { pageNumber: 0, pageSize: environment.pageSize, totalElements: rows.length };

                    if (response.headers) {
                        paginationInfoResult = {
                            pageSize: parseInt(response.headers.get(CustomHeader.pageSize) ?? environment.pageSize.toString()),
                            pageNumber: parseInt(response.headers.get(CustomHeader.pageIndex) ?? '1'),
                            totalElements: parseInt(response.headers.get(CustomHeader.totalCount) ?? '0') ?? rows.length,
                        };
                    }

                    return {
                        result: rows,
                        paginationInfo: paginationInfoResult,
                    };
                })
            );
    }

    /**
     * Performs a PATCH request to update a resource partially.
     * @param endpoint The API endpoint to call.
     * @param entity The partial entity to update.
     * @param params Optional query parameters to include in the request.
     * @returns An Observable of the updated resource.
     */
    patch<T>(endpoint: string, entity: Partial<T>, params: Record<string, string> = {}): Observable<T> {
        const headers = {
            'Content-Type': 'application/json',
        };
        return this.http.patch<T>(endpoint, entity, { headers, params: { ...params, organizationUUID: environment.organizationUUID } });
    }

    /**
     * Performs a POST request to create a new resource.
     * @param endpoint The API endpoint to call.
     * @param body The data to send in the request body.
     * @param params Optional query parameters to include in the request.
     * @returns An Observable of the created resource.
     */
    post<T, R = T>(endpoint: string, body: Partial<T>, params: Record<string, string> = {}, headers = { 'Content-Type': 'application/json' } as Record<string,string>): Observable<R> {
        return this.http.post<R>(endpoint, body, { headers, params: { ...params, organizationUUID: environment.organizationUUID } });
    }

    /**
     * Performs a PUT request to update or replace a resource.
     * @param endpoint The API endpoint to call.
     * @param body The data to send in the request body.
     * @param params Optional query parameters to include in the request.
     * @returns An Observable of the updated resource.
     */
    put<T, R = T>(endpoint: string, body: Partial<T>, params: Record<string, string> = {}): Observable<R> {
        const headers = {
            'Content-Type': 'application/json',
        };
        return this.http.put<R>(endpoint, body, { headers, params: { ...params, organizationUUID: environment.organizationUUID } });
    }

    /**
     * Performs a DELETE request to remove a resource.
     * @param endpoint The API endpoint to call.
     * @param uuid The unique identifier of the resource to delete.
     * @param params Optional query parameters to include in the request.
     * @returns An Observable of the delete operation result.
     */
    delete(endpoint: string, uuid: string, params: Record<string, string> = {}) {
        const headers = {
            'Content-Type': 'application/json',
        };
        return this.http.delete(endpoint + uuid, { headers, params: { ...params, organizationUUID: environment.organizationUUID } });
    }

    //funzione che carica un asset sulla medialibrary
    public createAsset<T extends { file?: File | undefined }, K>(asset: T, parts: string[]): Observable<K> {
        const fd = this.toFormData<T>(asset);
        const params = {
            organizationUUID: environment.organizationUUID,
            parts: parts.join(','),
        };
        return this.http.post<K>(environment.mediaLibraryUrl, fd, { params: params });
    }

    private toFormData<T extends { file?: File }>(data: T): FormData {
        const fd = new FormData();

        const jsonData: any = { ...data };
        if (jsonData['previewUUID'] === undefined) {
            jsonData['previewUUID'] = null;
        }

        delete jsonData['_id'];
        delete jsonData['file']; // do not serialize a file in JSON format!!
        fd.append('assetData', JSON.stringify(jsonData));

        if (data.file) fd.append('file', data.file);

        return fd;
    }
}
